import React, { useEffect, Suspense, lazy } from "react";
import { PageQueryData } from "../../templates/PageTypes";
import Seo, { SeoProps } from "./SEO";
import { KontentSiteFrame } from "../../types";
import { PageProps } from "gatsby";
import EmbedContainer from "../blocks/Embed";
import { LoadingTile } from "../elements/LoadingTile/LoadingTile";
import TimeSensitiveEmbedContainer from "../blocks/TimeSensitiveEmbed";
import { PreviewOverlay } from "../blocks/PreviewOverlay";
const HeaderContainer = lazy(() => import("../siteframe/Header"));
const FooterContainer = lazy(() => import("../siteframe/Footer"));
const AlertContainer = lazy(() => import("../siteframe/Alert"));
const KontentSmartLinks = lazy(() => import("../containers/KontentSmartLinks"));
// require() b/c typescript compiler ignores css import statement
// if import not assigned in component
require("../../css/_main.css");

interface Props {
  children?: React.ReactNode | readonly React.ReactNode[];
  siteFrame?: KontentSiteFrame;
  seo: SeoProps;
  readonly location: PageProps["location"];
}

const MainLayout: React.FC<Props> = ({
  location,
  siteFrame,
  children,
  seo,
}) => {
  const today = new Date();
  const showPreviewIndicator = process.env.GATSBY_USE_PREVIEW_OVERLAY;

  const alert = siteFrame?.value[0]?.elements.alert.value
    .sort((leftAlert, rightAlert) => {
      const leftDate = leftAlert.elements?.publish_date?.value;
      const rightDate = rightAlert.elements?.publish_date?.value;

      if (leftDate < rightDate) {
        return 1;
      }

      if (leftDate > rightDate) {
        return -1;
      }

      return 0;
    })
    .find((alert) => {
      const publish = new Date(parseInt(alert.elements?.publish_date?.value));
      const unpublish = new Date(
        parseInt(alert.elements?.unpublish_date?.value)
      );

      if (!!unpublish && today > unpublish) {
        return false;
      }

      if (today > publish) {
        return true;
      }

      return false;
    });

  return (
    <main
      data-kontent-project-id={process.env.GATSBY_KONTENT_PROJECT_ID}
      data-kontent-language-codename="default"
    >
      {process.env.GATSBY_ENABLE_KONTENT_PREVIEW ? (
        <Suspense fallback={<LoadingTile />}>
          <KontentSmartLinks />
        </Suspense>
      ) : null}
      <Seo {...seo} />
      <Suspense fallback={<LoadingTile />}>
        {alert && <AlertContainer {...alert.elements} id={alert?.system?.id} />}
      </Suspense>
      <Suspense fallback={<LoadingTile />}>
        <HeaderContainer
          header={siteFrame?.value[0]?.elements?.header?.value[0]}
          location={location}
        />
      </Suspense>
      {children}
      {showPreviewIndicator && <PreviewOverlay />}

      {siteFrame?.value[0]?.elements?.embeds?.value?.map((embed, idx) => {
        if (embed.internal?.type === "kontent_item_embed") {
          return <EmbedContainer {...embed?.elements} key={`embed-${idx}`} />;
        }

        if (embed.internal?.type === "kontent_item_time_sensitive_embed") {
          return (
            <TimeSensitiveEmbedContainer
              {...embed?.elements}
              key={`embed-${idx}`}
            />
          );
        }
      })}
      <Suspense fallback={<LoadingTile />}>
        <FooterContainer
          footer={siteFrame?.value[0]?.elements?.footer?.value[0]}
        />
      </Suspense>
    </main>
  );
};

export default MainLayout;
